import { create } from 'zustand'
import type { Job } from '@/types/job'
import { jobsApi } from '@/services/api/jobs'

interface JobStore {
  jobs: Job[]
  activeJobs: Job[]
  isLoading: boolean
  error: string | null
  loadJobs: () => Promise<void>
  loadJob: (id: string) => Promise<Job>
  createJob: (jobType: 'compile' | 'execute' | 'backtest', config: Record<string, unknown>) => Promise<Job>
  cancelJob: (id: string) => Promise<void>
  retryJob: (id: string) => Promise<void>
  removeJob: (id: string) => void
}

export const useJobStore = create<JobStore>((set) => ({
  jobs: [],
  activeJobs: [],
  isLoading: false,
  error: null,

  loadJobs: async () => {
    set({ isLoading: true, error: null })
    try {
      const result = await jobsApi.list()
      // Extract jobs array from paginated response
      const jobsArray = result.jobs || []
      const activeJobs = jobsArray.filter(
        (j) => j.status === 'queued' || j.status === 'processing'
      )
      set({ jobs: jobsArray, activeJobs, isLoading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load jobs',
        isLoading: false,
        jobs: [],
        activeJobs: [],
      })
    }
  },

  loadJob: async (id: string) => {
    try {
      const job = await jobsApi.get(id)
      set((state) => ({
        jobs: state.jobs.map((j) => (j.id === id ? job : j)),
      }))
      return job
    } catch (error) {
      throw error
    }
  },

  createJob: async (jobType, config) => {
    set({ isLoading: true, error: null })
    try {
      const job = await jobsApi.create({ jobType, config })
      set((state) => ({
        jobs: [job, ...state.jobs],
        activeJobs: [job, ...state.activeJobs],
        isLoading: false,
      }))
      return job
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create job',
        isLoading: false,
      })
      throw error
    }
  },

  cancelJob: async (id: string) => {
    try {
      await jobsApi.cancel(id)
      set((state) => ({
        jobs: state.jobs.map((j) =>
          j.id === id ? { ...j, status: 'cancelled' as const } : j
        ),
        activeJobs: state.activeJobs.filter((j) => j.id !== id),
      }))
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to cancel job',
      })
      throw error
    }
  },

  retryJob: async (id: string) => {
    try {
      const job = await jobsApi.retry(id)
      set((state) => ({
        jobs: state.jobs.map((j) => (j.id === id ? job : j)),
        activeJobs: job.status === 'queued' || job.status === 'processing' 
          ? [...state.activeJobs, job]
          : state.activeJobs,
      }))
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to retry job',
      })
      throw error
    }
  },

  removeJob: (id: string) => {
    set((state) => ({
      jobs: state.jobs.filter((j) => j.id !== id),
      activeJobs: state.activeJobs.filter((j) => j.id !== id),
    }))
  },
}))
